import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { containsValues, query } from '@/api/looker/utils/query';
import { AlertStatus } from '@/entities/alert-status';
import { useLookerModelId } from '@/hooks/looker/use-looker-model-id';
import { useLookerSdk } from '@/hooks/useLookerSdk';

import type { OldSitemapSite } from '../entities/site';
import type { Unit } from '../entities/unit';

export type UseSitesListOptions = {
  siteIds: string[];
};

function compareId(unit: Unit): string {
  return `${unit.siteId}-${unit.topId}-${unit.id}`;
}

function groupUnits(units: Unit[]): OldSitemapSite[] {
  const groupUnits: Record<string, Record<string, Unit[]>> = {};

  for (const unit of units) {
    if (!groupUnits[unit.siteId]) {
      groupUnits[unit.siteId] = {};
    }
    if (!groupUnits[unit.siteId][unit.topId]) {
      groupUnits[unit.siteId][unit.topId] = [];
    }

    groupUnits[unit.siteId][unit.topId].push(unit);
  }

  return Object.entries(groupUnits).map(([siteId, tops]) => ({
    id: siteId,
    tops: Object.entries(tops).map(([topId, units]) => ({
      id: topId,
      units,
    })),
  }));
}

export function useSitesList({
  siteIds,
}: UseSitesListOptions): UseQueryResult<OldSitemapSite[]> {
  const coreSDK = useLookerSdk();
  const model = useLookerModelId();

  return useQuery({
    queryKey: ['api/looker/site-map-sites-list', siteIds],
    async queryFn() {
      const units = await query(coreSDK)
        .from<{
          'frontend_unit_id_mapping.frontend_unit_id': string;
          'platform_core_flag_unit_status.suggested_action': string;
          'platform_core_flag_unit_status.status': AlertStatus;
          'frontend_top_id_mapping.frontend_top_id': string;
          'frontend_top_id_mapping.frontend_site_id': string;
        }>({
          model,
          view: 'platform_core_flag_unit_status',
          fields: [
            'platform_core_flag_unit_status_changed.status_changed_date_date',
            'frontend_unit_id_mapping.frontend_unit_id',
            'platform_core_flag_unit_status.status',
            'platform_core_flag_unit_status.suggested_action',
            'platform_core_flag_unit_status.unit_score',
            'frontend_top_id_mapping.frontend_top_id',
            'frontend_top_id_mapping.frontend_site_id',
          ],
          filters: {
            'platform_core_flag_top_status.frontend_group_id':
              containsValues(siteIds),
          },
          sorts: ['frontend_unit_id_mapping.frontend_unit_id asc'],
        })
        .toMany<Unit>((result) => ({
          id: result['frontend_unit_id_mapping.frontend_unit_id'],
          topId: result['frontend_top_id_mapping.frontend_top_id'],
          siteId: result['frontend_top_id_mapping.frontend_site_id'],
          alert: {
            status: result['platform_core_flag_unit_status.status'],
            suggestedAction:
              result['platform_core_flag_unit_status.suggested_action'],
          },
        }));

      units.sort((a, b) => compareId(a).localeCompare(compareId(b)));

      const sites = groupUnits(units);

      return sites;
    },
  });
}
